import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  LinearProgress,
  Alert,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Grid,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartRounded';
import SellIcon from '@mui/icons-material/SellRounded';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import StopIcon from '@mui/icons-material/StopRounded';
import TradingTaskModal from '../components/modals/TradingTaskModal';
import TradingStatsChart from '../components/charts/TradingStatsChart';
import BotStatusCard from '../components/trading/BotStatusCard';
import { ReconnectingWebSocket } from '../socket';
import { BotMap, IBuyBack } from '../shared/types';
import { SurfaceCard } from '../components/ui/SurfaceCard';
import { SectionHeader } from '../components/ui/SectionHeader';
import { Metric } from '../components/ui/Metric';
import { StatusPill, StatusTone } from '../components/ui/StatusPill';
import { formatPairPrice, formatTokenAmount, formatUsdOrDash } from '../utils/formatters';
import { tokens } from '../theme';

/**
 * Open tasks of one pair (backend `PendingBuybackSummary`). Buy figures are quote amounts, sell figures
 * base amounts: shown apart, never added. Older backends send buys only (no buy/sell fields).
 */
interface PendingPairSummary {
  baseAsset: string;
  quoteAsset: string;
  /** Open tasks of both kinds. */
  count: number;
  /** Buys: quote still to spend. */
  remainingBudget: number;
  totalSpent: number;
  avgPrice: number;
  buyCount?: number;
  sellCount?: number;
  /** Sells: base still to sell. */
  remainingSellBase?: number;
}

interface TradingViewProps {
  socket: ReconnectingWebSocket;
  botMap: BotMap;
  botTokens: Record<string, string[]>;
  buybacksData?: {
    completed: IBuyBack[];
    pending: IBuyBack[];
  };
  tradingActivityBuybacksData?: {
    completed: IBuyBack[];
    pending: IBuyBack[];
  };
  pendingBuybacksSummary?: Record<string, PendingPairSummary>;
  onRefresh?: () => void;
}

type BotType = 'dipster' | 'dumper';
type TaskFilter = 'all' | BotType;
type TaskStatus = 'all' | 'pending' | 'active' | 'completed';

/** Bot identity (never used for values or status). */
const BOT_IDENTITY: Record<BotType, { label: string; color: string }> = {
  dipster: { label: 'Dipster', color: tokens.chart.green },
  dumper: { label: 'Dumper', color: tokens.chart.violet },
};

/** Generic instance ids; any other botId is the bot the task is attributed to. */
const INSTANCE_BOT_IDS = ['DIPSTER', 'DUMPER'];

// DUMPER sell tasks may carry the wrong label, so botId wins for the dumper.
const getBotType = (task: IBuyBack): BotType | null => {
  if (task.label === 'dumper' || task.botId === 'DUMPER') return 'dumper';
  if (task.label === 'dipster' || task.botId === 'DIPSTER') return 'dipster';
  return null;
};

/** Pair prices, as on the bot cards: 4 decimals below $1 ($0.0919), cents from $1 up; null when unknown. */
const formatPrice = (price: number | undefined, base: string, quote: string) => {
  if (price == null || !Number.isFinite(price) || price <= 0) return null;
  return formatPairPrice(price, base, quote, price < 1 ? 4 : 2);
};

/** USD amounts: the shared card rule (formatUsd); zero reads "—". */
const usd = formatUsdOrDash;

/** Not applicable / nothing yet: "—" in the disabled text colour. */
const Dash: React.FC = () => (
  <Box component="span" sx={{ color: 'text.disabled' }}>
    —
  </Box>
);

/** Unit after an amount: small muted suffix ("102,509 RIF"), as in the Buybacks stats. */
const Unit: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ ml: 0.5, fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', letterSpacing: 0 }}>
    {children}
  </Box>
);

/** USD figure, or a disabled "—" for zero. */
const UsdValue: React.FC<{ value: number | null | undefined }> = ({ value }) => (value ? <>{usd(value)}</> : <Dash />);

/** Token amount with its unit, or a disabled "—" for zero. */
const TokenValue: React.FC<{ value: number | null | undefined; unit: string }> = ({ value, unit }) =>
  value ? (
    <>
      {formatTokenAmount(value)}
      <Unit>{unit}</Unit>
    </>
  ) : (
    <Dash />
  );

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** Badge of a pair summary: "2 buys · 1 sell"; "3 tasks" from backends without the split. */
const taskMix = (s: PendingPairSummary) => {
  if (s.buyCount == null && s.sellCount == null) return plural(s.count, 'task');
  const parts = [s.buyCount ? plural(s.buyCount, 'buy') : '', s.sellCount ? plural(s.sellCount, 'sell') : ''].filter(Boolean);
  return parts.length ? parts.join(' · ') : plural(s.count, 'task');
};

/** Sells get their own figure (base amount) when the pair has any. */
const hasSells = (s: PendingPairSummary) => (s.sellCount ?? 0) > 0 || (s.remainingSellBase ?? 0) > 0;

/** Pair as written everywhere: "RIF / USDT". */
const pairLabel = (task: IBuyBack) => `${task.baseAsset} / ${task.quoteAsset}`;

/** In progress = gold, failed = red, done / paused / not started = neutral (not started gets a hollow dot, see below). */
const getStatusTone = (status: string): StatusTone => {
  switch (status) {
    case 'ACTIVE':
    case 'EXECUTING':
      return 'brand';
    case 'FAILED':
      return 'error';
    default:
      return 'neutral';
  }
};

/** Identity (not status): neutral pill with a small dot in the bot's colour. */
const IdentityPill: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <StatusPill
    tone="neutral"
    size="sm"
    label={label}
    icon={<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />}
  />
);

const TradingView: React.FC<TradingViewProps> = ({
  socket,
  botMap,
  botTokens,
  buybacksData,
  tradingActivityBuybacksData,
  pendingBuybacksSummary,
  onRefresh,
}) => {
  const theme = useTheme();
  const [modalBotType, setModalBotType] = useState<'dipster' | 'dumper' | 'generic' | null>(null);
  const [taskFilter, setTaskFilter] = useState<TaskFilter>('all');
  const [statusFilter, setStatusFilter] = useState<TaskStatus>('all');
  const [confirmStop, setConfirmStop] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const pending = useMemo(() => buybacksData?.pending ?? [], [buybacksData?.pending]);
  const completed = useMemo(() => buybacksData?.completed ?? [], [buybacksData?.completed]);
  const tradingActivityPending = useMemo(() => tradingActivityBuybacksData?.pending ?? pending, [tradingActivityBuybacksData?.pending, pending]);
  const tradingActivityCompleted = useMemo(() => tradingActivityBuybacksData?.completed ?? completed, [tradingActivityBuybacksData?.completed, completed]);

  // RIF_BUY and RIF_SELL tasks only (not DCA), open and completed, so every status filter can match.
  const tradingTasks = useMemo(
    () => [...pending, ...completed].filter(t => t.taskType === 'RIF_BUY' || t.taskType === 'RIF_SELL'),
    [pending, completed]
  );
  
  const tradingActivityTasks = useMemo(() =>
    tradingActivityPending.filter(t => t.taskType === 'RIF_BUY' || t.taskType === 'RIF_SELL'),
  [tradingActivityPending]);

  const tradingActivityCompletedTasks = useMemo(() =>
    tradingActivityCompleted.filter(t => t.taskType === 'RIF_BUY' || t.taskType === 'RIF_SELL'),
  [tradingActivityCompleted]);

  // Filter pendingBuybacksSummary to only show RIF
  const rifPendingSummary = useMemo(() => 
    pendingBuybacksSummary ? 
      Object.fromEntries(
        Object.entries(pendingBuybacksSummary).filter(([key]) => key.startsWith('RIF/'))
      ) : undefined,
  [pendingBuybacksSummary]);

  const filteredTasks = useMemo(() => {
    let tasks = [...tradingTasks];

    if (taskFilter !== 'all') {
      tasks = tasks.filter(t => getBotType(t) === taskFilter);
    }

    if (statusFilter !== 'all') {
      tasks = tasks.filter(t => t.status?.toLowerCase() === statusFilter);
    }

    return tasks.sort((a, b) => (b.lastUpdatedAt || 0) - (a.lastUpdatedAt || 0));
  }, [tradingTasks, taskFilter, statusFilter]);

  // Pause/Resume handlers
  const handlePauseAll = useCallback((botType: BotType) => {
    socket?.send('pauseAllTasks', { botType });
    setSnackbar({ open: true, message: `Pausing all ${BOT_IDENTITY[botType].label} tasks…`, severity: 'success' });
    setTimeout(() => onRefresh?.(), 1000);
  }, [socket, onRefresh]);

  const handleResumeAll = useCallback((botType: BotType) => {
    socket?.send('resumeAllTasks', { botType });
    setSnackbar({ open: true, message: `Resuming all ${BOT_IDENTITY[botType].label} tasks…`, severity: 'success' });
    setTimeout(() => onRefresh?.(), 1000);
  }, [socket, onRefresh]);

  const handleStopAll = useCallback(() => {
    socket?.send('stopAllTasks', {});
    setSnackbar({ open: true, message: 'Stopping all trading tasks…', severity: 'success' });
    setTimeout(() => onRefresh?.(), 1000);
  }, [socket, onRefresh]);

  const handleSuccess = useCallback((message?: string) => {
    if (message) {
      setSnackbar({ open: true, message, severity: 'success' });
    }
    onRefresh?.();
  }, [onRefresh]);

  const filterGroupSx = { flexWrap: 'wrap' as const };

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
        spacing={2}
        sx={{ mb: { xs: 2.5, md: 3 } }}
      >
        <Box>
          <Typography component="h1" sx={{ fontSize: { xs: '1.625rem', md: '2rem' }, fontWeight: 750, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
            Trading control center
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: { xs: '0.875rem', md: '0.9375rem' }, color: 'text.secondary' }}>
            RIF trading across the Dipster and Dumper instances
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh data">
            <IconButton
              onClick={onRefresh}
              aria-label="Refresh data"
              sx={{ width: 44, height: 44, border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`, bgcolor: 'rgba(255,255,255,0.04)' }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="error"
            startIcon={<StopIcon />}
            onClick={() => setConfirmStop(true)}
            sx={{
              // Same 44px height as the refresh button next to it.
              minHeight: 44,
              borderColor: alpha(theme.palette.error.main, 0.4),
              bgcolor: alpha(theme.palette.error.main, 0.06),
              '&:hover': { borderColor: theme.palette.error.main, bgcolor: alpha(theme.palette.error.main, 0.12) },
            }}
          >
            Emergency stop
          </Button>
        </Stack>
      </Stack>

      {/* Bot Status Cards */}
      <Grid container spacing={{ xs: 2, md: 2.5 }} mb={{ xs: 2.5, md: 3 }}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
          <BotStatusCard
            type="dipster"
            pendingTasks={pending}
            onPauseAll={() => handlePauseAll('dipster')}
            onResumeAll={() => handleResumeAll('dipster')}
            onAddTask={() => setModalBotType('dipster')}
            socket={socket}
            onConfigSuccess={(msg) => setSnackbar({ open: true, message: msg, severity: 'success' })}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
          <BotStatusCard
            type="dumper"
            pendingTasks={pending}
            onPauseAll={() => handlePauseAll('dumper')}
            onResumeAll={() => handleResumeAll('dumper')}
            onAddTask={() => setModalBotType('dumper')}
            socket={socket}
            onConfigSuccess={(msg) => setSnackbar({ open: true, message: msg, severity: 'success' })}
          />
        </Grid>
      </Grid>

      {/* Trading Activity Chart */}
      <TradingStatsChart
        completedTasks={tradingActivityCompletedTasks}
        pendingTasks={tradingActivityTasks}
        sx={{ mb: { xs: 2.5, md: 3 } }}
      />

      {/* Pending Summary by Asset */}
      {rifPendingSummary && Object.keys(rifPendingSummary).length > 0 && (
        <Box mb={{ xs: 2.5, md: 3 }}>
          <SectionHeader variant="page" title="Pending by trading pair" />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
              gap: 2,
            }}
          >
            {Object.entries(rifPendingSummary).map(([key, stats]) => (
              <SurfaceCard key={key}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>
                    {stats.baseAsset} / {stats.quoteAsset}
                  </Typography>
                  <StatusPill tone="neutral" size="sm" label={taskMix(stats)} dot={false} />
                </Stack>
                {/* Buys are quote budgets, sells base amounts: two figures, never one sum. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
                  <Metric
                    label="Pending buys"
                    value={<TokenValue value={stats.remainingBudget} unit={stats.quoteAsset} />}
                    size="sm"
                  />
                  {hasSells(stats) && (
                    <Metric
                      label="Pending sells"
                      value={<TokenValue value={stats.remainingSellBase} unit={stats.baseAsset} />}
                      size="sm"
                    />
                  )}
                </Box>
              </SurfaceCard>
            ))}
          </Box>
        </Box>
      )}

      {/* Tasks */}
      <SectionHeader
        variant="page"
        title="Tasks"
        subtitle={`${filteredTasks.length} of ${tradingTasks.length} shown`}
      />
      <Stack direction="row" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={taskFilter}
          onChange={(_, v) => v && setTaskFilter(v)}
          aria-label="Bot filter"
          sx={filterGroupSx}
        >
          <ToggleButton value="all">All bots</ToggleButton>
          <ToggleButton value="dipster">Dipster</ToggleButton>
          <ToggleButton value="dumper">Dumper</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => v && setStatusFilter(v)}
          aria-label="Status filter"
          sx={filterGroupSx}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <SurfaceCard padding="none" sx={{ overflow: 'hidden', containerType: 'inline-size' }}>
        {filteredTasks.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No tasks found matching the current filters.</Typography>
          </Box>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: `1px solid ${tokens.borderSubtle}` }} />}>
            {filteredTasks.map((task) => {
              const status = task.status || 'PENDING';
              const isSell = task.taskType === 'RIF_SELL';
              const botType = getBotType(task);
              const identity = botType ? BOT_IDENTITY[botType] : null;
              // Tasks attributed to a specific bot keep its name next to the instance pill.
              const attribution = INSTANCE_BOT_IDS.includes(task.botId)
                ? null
                : botMap[task.botId]?.name ?? task.botId;
              const progressValue = isSell
                ? (task.totalBaseBought || 0)
                : (task.totalQuoteSpent || 0);
              const progress = task.targetAmount > 0
                ? (progressValue / task.targetAmount) * 100
                : 0;

              return (
                <Box
                  key={task.uuid}
                  sx={{
                    // Same insets as the cards above (20px phones, 24px from md), so left edges line up.
                    px: { xs: 2.5, md: 3 },
                    py: 2,
                    display: 'grid',
                    alignItems: 'center',
                    columnGap: 1.5,
                    rowGap: 1.5,
                    // Layout follows the card width (container query), not the viewport:
                    // stacked on phones, two rows on tablets, one row once the progress
                    // column has room for its captions.
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    gridTemplateAreas: `"task status" "progress progress" "stats stats"`,
                    '@container (min-width: 600px)': {
                      columnGap: 3,
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gridTemplateAreas: `"task status" "progress stats"`,
                    },
                    '@container (min-width: 960px)': {
                      gridTemplateColumns: 'minmax(180px, 220px) minmax(0, 1fr) 290px 92px',
                      gridTemplateAreas: `"task progress stats status"`,
                    },
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                  }}
                >
                  {/* Task Info */}
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ gridArea: 'task', minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: 'rgba(255,255,255,0.06)',
                        border: `1px solid ${tokens.borderSubtle}`,
                        color: 'text.secondary',
                        flexShrink: 0,
                      }}
                    >
                      {isSell ? <SellIcon fontSize="small" /> : <ShoppingCartIcon fontSize="small" />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 650 }} noWrap>
                        {isSell ? 'Sell' : 'Buy'} {pairLabel(task)}
                      </Typography>
                      {(identity || attribution) && (
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5, minWidth: 0 }}>
                          {identity && <IdentityPill label={identity.label} color={identity.color} />}
                          {attribution && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {attribution}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Box>
                  </Stack>

                  {/* Progress */}
                  <Box sx={{ gridArea: 'progress', minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} mb={0.75}>
                      {/* Sells in RIF (amount + unit), buys in USD (spent / budget). */}
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {isSell ? 'Sold' : 'Spent'}{' '}
                        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                          {isSell ? (
                            <TokenValue value={task.totalBaseBought} unit={task.baseAsset} />
                          ) : (
                            <UsdValue value={task.totalQuoteSpent} />
                          )}
                        </Box>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Target{' '}
                        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                          {isSell ? (
                            <TokenValue value={task.targetAmount} unit={task.baseAsset} />
                          ) : (
                            <UsdValue value={task.targetAmount} />
                          )}
                        </Box>
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(progress, 100)}
                      aria-label={`${progress.toFixed(1)}% complete`}
                      sx={{
                        height: 6,
                        bgcolor: 'rgba(255,255,255,0.08)',
                        '& .MuiLinearProgress-bar': { background: tokens.goldGradient },
                      }}
                    />
                  </Box>

                  {/* Stats: fixed columns so each metric lines up across rows */}
                  <Box
                    sx={{
                      gridArea: 'stats',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      columnGap: 2,
                      minWidth: 0,
                    }}
                  >
                    <Metric label="Avg price" value={formatPrice(task.avgBuyPrice, task.baseAsset, task.quoteAsset) ?? <Dash />} size="sm" />
                    <Metric label="Progress" value={progress > 0 ? `${progress.toFixed(1)}%` : <Dash />} size="sm" />
                    {task.adaptiveBuyRate != null && task.adaptiveBuyRate > 0 && (
                      <Tooltip title={`Market Buy Ratio: ${((task.marketBuyRatio || 0) * 100).toFixed(1)}% — ${(task.marketBuyRatio || 0) > 0.6 ? 'Strong organic buying → reduced size' : (task.marketBuyRatio || 0) > 0.4 ? 'Neutral → moderate size' : 'Weak buying → bot compensating'}`}>
                        <Box sx={{ cursor: 'help', minWidth: 0 }}>
                          <Metric label="Rate" value={`$${task.adaptiveBuyRate.toFixed(0)}/min`} size="sm" />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Status */}
                  <Box sx={{ gridArea: 'status', justifySelf: 'end' }}>
                    <StatusPill
                      tone={getStatusTone(status)}
                      label={status.charAt(0) + status.slice(1).toLowerCase()}
                      size="sm"
                      pulsing={status === 'ACTIVE'}
                      hollowDot={status === 'PENDING'}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </SurfaceCard>

      {/* Modal */}
      <TradingTaskModal
        isOpen={modalBotType !== null}
        onClose={() => setModalBotType(null)}
        onSuccess={handleSuccess}
        botMap={botMap}
        botTokens={botTokens}
        socket={socket}
        defaultBotType={modalBotType === 'dipster' || modalBotType === 'dumper' ? modalBotType : undefined}
      />

      {/* Emergency stop confirmation */}
      <Dialog open={confirmStop} onClose={() => setConfirmStop(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Stop all trading tasks?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Every Dipster and Dumper task will be stopped immediately. Use this only in an emergency.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button color="inherit" onClick={() => setConfirmStop(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<StopIcon />}
            onClick={() => {
              setConfirmStop(false);
              handleStopAll();
            }}
          >
            Stop all tasks
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TradingView;
